import { ref } from 'vue';

import { administrationApiSdk } from '../sdk/administrationClient.js';

import { defineCollectionStore } from './collectionStore.js';

/**
 * Auction Operations — the brand an auction runs under.
 *
 * It carries the Operation-wide knockouts, which are read live from this
 * document on every check and never from a house's copy. Both knockout actions
 * answer the whole updated document, so they hold it as `current` the way every
 * other write does.
 */
export const useAuctionOperationsStore = defineCollectionStore({
    id: 'administration-auction-operations',
    client: administrationApiSdk.auctionOperations,
    extend({ client, attempt, holdCurrent }) {
        // The houses this Operation manages. A page of its own, kept apart from
        // `documents`: a detail screen shows one Operation and its houses at the
        // same time, and one list ref cannot hold both.
        const auctionHouses = ref([]);
        const auctionHouseCount = ref(0);

        async function loadAuctionHouses(id, pagination) {
            const attempted = await attempt(function () {
                return client.listAuctionHouses(id, pagination);
            });
            if (attempted.ok === false) {
                return false;
            }
            auctionHouses.value = attempted.value.documents;
            auctionHouseCount.value = attempted.value.meta.totalCount;
            return true;
        }

        return {
            auctionHouses,
            auctionHouseCount,
            loadAuctionHouses,

            setLegalCompany: (id, companyId) => holdCurrent(() => client.setLegalCompany(id, companyId)),
            setManagementCompany: (id, companyId) => holdCurrent(() => client.setManagementCompany(id, companyId)),
            setBillingCompany: (id, companyId, subscriptionId) => holdCurrent(
                () => client.setBillingCompany(id, companyId, subscriptionId)
            ),
            setPolicy: (id, policyName, policy) => holdCurrent(() => client.setPolicy(id, policyName, policy)),
            clearPolicy: (id, policyName) => holdCurrent(() => client.clearPolicy(id, policyName)),
            setKnockedOutHumans: (id, humans) => holdCurrent(() => client.setKnockedOutHumans(id, humans)),
            knockOutDealership: (id, dealershipId) => holdCurrent(() => client.knockOutDealership(id, dealershipId)),
            restoreDealership: (id, dealershipId) => holdCurrent(() => client.restoreDealership(id, dealershipId))
        };
    }
});

export default useAuctionOperationsStore;
